import { Modal, Pressable, ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { SubHeading, Caption, Body, ThemedText } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';

type WeatherSource = 'forecast' | 'historical';

export type WeatherAlert = {
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description?: string;
};

export type DayWeather = {
  iconName: string;
  label: string;
  tempMin?: number;
  tempMax?: number;
  precipProb?: number;
  windSpeed?: number;
  condition?: string;
  description?: string;
  source?: WeatherSource;
  alerts?: WeatherAlert[];
};

type DayWeatherSheetProps = {
  visible: boolean;
  onClose: () => void;
  date: Date;
  weather?: DayWeather;
};

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
};

export function DayWeatherSheet({ visible, onClose, date, weather }: DayWeatherSheetProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();

  const title =
    typeof weather?.tempMax === 'number' && typeof weather?.tempMin === 'number'
      ? `${weather.tempMax}° / ${weather.tempMin}°`
      : weather?.label || 'Weather';

  const subtitle =
    weather?.source === 'historical'
      ? 'Historical average (will update with forecast as the trip approaches)'
      : 'Forecast';

  const precip =
    typeof weather?.precipProb === 'number'
      ? `${Math.round(weather.precipProb)}% chance of precipitation`
      : undefined;

  const wind =
    typeof weather?.windSpeed === 'number' ? `Wind: ${Math.round(weather.windSpeed)} m/s` : undefined;

  const severityColor = (severity: WeatherAlert['severity']) => {
    if (severity === 'critical') return theme.colors.error;
    if (severity === 'high') return theme.colors.warning;
    if (severity === 'medium') return theme.colors.primary;
    return theme.colors.textSecondary;
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
      presentationStyle="overFullScreen"
      accessibilityViewIsModal
    >
      <View style={{ flex: 1, backgroundColor: theme.colors.modalOverlay, justifyContent: 'flex-end' }}>
        <Pressable style={{ flex: 1 }} onPress={onClose} accessibilityRole="button" accessibilityLabel="Close weather details" />

        <View
          style={{
            backgroundColor: theme.colors.surface,
            borderTopLeftRadius: theme.radii.xl,
            borderTopRightRadius: theme.radii.xl,
            maxHeight: '92%',
            overflow: 'hidden',
            paddingBottom: insets.bottom,
          }}
        >
          {/* Header */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: theme.spacing(2.5),
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.borderMuted,
            }}
          >
            <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
              <SubHeading>Weather</SubHeading>
              <Caption color={theme.colors.textSecondary}>{formatDate(date)}</Caption>
            </View>
            <Pressable
              onPress={onClose}
              style={({ pressed }) => ({
                padding: theme.spacing(0.75),
                borderRadius: theme.radii.md,
                opacity: pressed ? 0.7 : 1,
              })}
              accessibilityRole="button"
              accessibilityLabel="Close weather details"
            >
              <Ionicons name="close" size={22} color={theme.colors.text} />
            </Pressable>
          </View>

          <ScrollView
            bounces
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{
              padding: theme.spacing(2.5),
              paddingBottom: theme.spacing(3.5),
              gap: theme.spacing(2),
            }}
          >
            {!weather ? (
              <Body color={theme.colors.textSecondary}>Weather details are not available for this day yet.</Body>
            ) : (
              <>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                  <Ionicons name={weather.iconName as any} size={22} color={theme.colors.primary} />
                  <ThemedText variant="h3" style={{ fontSize: 18, fontWeight: '700' }}>
                    {title}
                  </ThemedText>
                </View>

                <Caption color={theme.colors.textSecondary}>{subtitle}</Caption>

                {!!(weather.condition || weather.description) && (
                  <View style={{ gap: theme.spacing(0.5) }}>
                    {!!weather.condition && (
                      <ThemedText variant="bodyStrong" style={{ fontSize: 14 }}>
                        {weather.condition}
                      </ThemedText>
                    )}
                    {!!weather.description && (
                      <Body color={theme.colors.textSecondary} style={{ fontSize: 13 }}>
                        {weather.description}
                      </Body>
                    )}
                  </View>
                )}

                {!!precip && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                    <Ionicons name="water-outline" size={18} color={theme.colors.textSecondary} />
                    <Body color={theme.colors.textSecondary} style={{ fontSize: 13 }}>
                      {precip}
                    </Body>
                  </View>
                )}

                {!!wind && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                    <Ionicons name="leaf-outline" size={18} color={theme.colors.textSecondary} />
                    <Body color={theme.colors.textSecondary} style={{ fontSize: 13 }}>
                      {wind}
                    </Body>
                  </View>
                )}

                {!!weather.alerts?.length && (
                  <View style={{ gap: theme.spacing(1) }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                      <Ionicons name="alert-circle-outline" size={18} color={theme.colors.warning} />
                      <ThemedText variant="bodyStrong" style={{ fontSize: 14 }}>
                        Alerts
                      </ThemedText>
                    </View>
                    <View style={{ gap: theme.spacing(1) }}>
                      {weather.alerts.slice(0, 3).map((a, idx) => (
                        <View
                          key={`${a.title}-${idx}`}
                          style={{
                            borderWidth: 1,
                            borderColor: theme.colors.borderMuted,
                            borderRadius: theme.radii.md,
                            padding: theme.spacing(1.25),
                            backgroundColor: theme.colors.surface,
                            gap: theme.spacing(0.5),
                          }}
                        >
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                            <Ionicons name="warning-outline" size={16} color={severityColor(a.severity)} />
                            <ThemedText variant="bodyStrong" style={{ flex: 1, fontSize: 13 }}>
                              {a.title}
                            </ThemedText>
                          </View>
                          {!!a.description && (
                            <Body color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                              {a.description}
                            </Body>
                          )}
                        </View>
                      ))}
                    </View>
                    {weather.alerts.length > 3 && (
                      <Caption color={theme.colors.textSecondary}>
                        {weather.alerts.length - 3} more alert{weather.alerts.length - 3 === 1 ? '' : 's'} not shown.
                      </Caption>
                    )}
                  </View>
                )}
              </>
            )}

            <Button label="Done" variant="secondary" onPress={onClose} />
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}


